import React, {memo, useCallback} from 'react';
import cn from 'classnames';
import styles from './TabSwitcher.css';

interface TabSwitcherProps<Value extends string = string> {
	classNames?: string;
	value: Value;
	options: {value: Value; iconUrl?: string; activeIconUrl?: string}[];
	getDisplayValue?(value: Value): string;
	onChange(value: Value): void;
}

const TabSwitcherInternal = <Value extends string = string>(props: TabSwitcherProps<Value>) => {
	const {classNames, value, options, getDisplayValue, onChange} = props;

	const handleSetValue = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			const newValue = event.currentTarget.dataset.value as Value;
			onChange(newValue);
		},
		[onChange],
	);

	return (
		<div className={cn(styles.root, classNames)}>
			{options.map(option => (
				<button
					key={option.value}
					className={cn(
						styles.option,
						getDisplayValue && styles.withText,
						option.value === value && styles.isCurrent,
					)}
					style={
						option.iconUrl
							? {
									backgroundImage: `url(${
										option.value === value ? option.activeIconUrl : option.iconUrl
									})`,
							  }
							: undefined
					}
					type='button'
					data-value={option.value}
					onClick={handleSetValue}>
					{getDisplayValue && getDisplayValue(option.value)}
				</button>
			))}
		</div>
	);
};

TabSwitcherInternal.displayName = 'TabSwitcher';

export const TabSwitcher = memo(TabSwitcherInternal) as typeof TabSwitcherInternal;
